import { useState, useMemo, useEffect } from 'react';
import { Box, Paper, Typography, Chip, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { hashStr } from './DeviceDetail';

// ── RMF Control Families ───────────────────────────────────────

const FAMILIES = [
  { code: 'AC', name: 'Access Control' },
  { code: 'AU', name: 'Audit & Accountability' },
  { code: 'CA', name: 'Assessment & Authorization' },
  { code: 'CM', name: 'Configuration Management' },
  { code: 'IA', name: 'Identification & Auth' },
  { code: 'IR', name: 'Incident Response' },
  { code: 'MA', name: 'Maintenance' },
  { code: 'MP', name: 'Media Protection' },
  { code: 'PE', name: 'Physical & Environmental' },
  { code: 'PL', name: 'Planning' },
  { code: 'RA', name: 'Risk Assessment' },
  { code: 'SA', name: 'System & Services Acq.' },
  { code: 'SC', name: 'Comms Protection' },
  { code: 'SI', name: 'System & Info Integrity' },
];

const CONTROL_NAMES = {
  AC: ['Account Management', 'Access Enforcement', 'Information Flow Enforcement', 'Separation of Duties', 'Least Privilege', 'Unsuccessful Login Attempts', 'Session Lock'],
  AU: ['Audit Policy', 'Audit Record Content', 'Audit Storage Capacity', 'Response to Audit Failures', 'Audit Review & Analysis', 'Audit Reduction'],
  CA: ['Security Assessments', 'Plan of Action', 'Security Authorization', 'Continuous Monitoring', 'Penetration Testing'],
  CM: ['Baseline Configuration', 'Configuration Change Control', 'Security Impact Analysis', 'Access Restrictions for Change', 'Configuration Settings', 'Software Usage Restrictions'],
  IA: ['Identification & Auth Policy', 'Device Identification', 'Authenticator Management', 'Authenticator Feedback', 'Cryptographic Module Auth', 'Multi-Factor Auth'],
  IR: ['Incident Response Policy', 'Incident Response Training', 'Incident Response Testing', 'Incident Handling', 'Incident Monitoring'],
  MA: ['Maintenance Policy', 'Controlled Maintenance', 'Maintenance Tools', 'Remote Maintenance', 'Maintenance Personnel'],
  MP: ['Media Protection Policy', 'Media Access', 'Media Marking', 'Media Storage', 'Media Transport', 'Media Sanitization'],
  PE: ['Physical Access Auth', 'Physical Access Control', 'Access Control for Transmission', 'Visitor Control', 'Power Equipment'],
  PL: ['Security Planning Policy', 'System Security Plan', 'Rules of Behavior', 'Security Concept of Operations'],
  RA: ['Risk Assessment Policy', 'Security Categorization', 'Risk Assessment', 'Vulnerability Scanning', 'Privacy Impact Assessment'],
  SA: ['System Acquisition Policy', 'Allocation of Resources', 'System Dev Life Cycle', 'Acquisition Process', 'Information System Documentation'],
  SC: ['Comms Protection Policy', 'Denial of Service Protection', 'Boundary Protection', 'Cryptographic Key Establishment', 'Transmission Confidentiality', 'Network Disconnect'],
  SI: ['System Integrity Policy', 'Flaw Remediation', 'Malicious Code Protection', 'Spam Protection', 'Security Alerts', 'Software Firmware Integrity'],
};

const IMPLEMENTATIONS = ['System Specific', 'Inherited', 'Hybrid'];
const STATUSES = ['Met', 'Met', 'Met', 'Partial', 'Partial', 'Not Met'];

function buildControls(familyCode) {
  const names = CONTROL_NAMES[familyCode] || [];
  return names.map((name, i) => {
    const h = hashStr(`${familyCode}-${i}`);
    return {
      id: `${familyCode}-${String(i + 1).padStart(2, '0')}`,
      name,
      status: STATUSES[h % STATUSES.length],
      implementation: IMPLEMENTATIONS[h % IMPLEMENTATIONS.length],
      lastAssessed: `${2025 + (h % 2)}-${String((h >>> 4) % 12 + 1).padStart(2, '0')}-${String((h >>> 8) % 28 + 1).padStart(2, '0')}`,
    };
  });
}

const STATUS_COLOR = { Met: '#4caf50', Partial: '#ff9800', 'Not Met': '#f44336' };

// ── ColFilter ──────────────────────────────────────────────────

function ColFilter({ label, options, value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const isFiltered = value.length > 0;
  const toggle = (val) => onChange(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography sx={{ color: isFiltered ? '#00bcd4' : '#7a8fa6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}{isFiltered ? ` (${value.length})` : ''}
      </Typography>
      {options.length > 0 && (
        <>
          <Box onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: isFiltered ? '#00bcd4' : '#1e3a5f', '&:hover': { color: '#00bcd4' } }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
          </Box>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
            PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', minWidth: 160 } }}>
            {isFiltered && (
              <MenuItem onClick={() => { onChange([]); setAnchor(null); }} sx={{ fontSize: 11, color: '#f44336', borderBottom: '1px solid #1e3a5f', '&:hover': { backgroundColor: '#0d2137' } }}>
                Clear filter
              </MenuItem>
            )}
            {options.map(opt => {
              const selected = value.includes(opt);
              return (
                <MenuItem key={opt} onClick={() => toggle(opt)}
                  sx={{ fontSize: 12, color: selected ? '#00bcd4' : '#c0cfe0', backgroundColor: selected ? '#0d2137' : 'transparent', '&:hover': { backgroundColor: '#0d2137' }, display: 'flex', justifyContent: 'space-between' }}>
                  {opt}
                  {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00bcd4' }} />}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
}

function StatBox({ label, value, color }) {
  return (
    <Paper sx={{ flex: 1, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px 20px', textAlign: 'center' }}>
      <Typography sx={{ color: color || '#ffffff', fontSize: 36, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 0.5 }}>{label}</Typography>
    </Paper>
  );
}

export default function AtoRmf() {
  const [selectedFamily, setSelectedFamily] = useState('AC');
  const [filterControlStatus, setFilterControlStatus] = useState([]);
  const [filterImpl, setFilterImpl] = useState([]);

  useEffect(() => {
    setFilterControlStatus([]);
    setFilterImpl([]);
  }, [selectedFamily]);

  const allControls = useMemo(() => {
    const all = FAMILIES.flatMap(f => buildControls(f.code));
    return {
      met:     all.filter(c => c.status === 'Met').length,
      partial: all.filter(c => c.status === 'Partial').length,
      notMet:  all.filter(c => c.status === 'Not Met').length,
      total:   all.length,
    };
  }, []);

  const familySummaries = useMemo(() =>
    FAMILIES.map(f => {
      const controls = buildControls(f.code);
      return { ...f, controls, met: controls.filter(c => c.status === 'Met').length };
    }), []);

  const activeControls = useMemo(() => buildControls(selectedFamily), [selectedFamily]);
  const activeFamily = FAMILIES.find(f => f.code === selectedFamily);

  const displayControls = useMemo(() => activeControls.filter(c => {
    if (filterControlStatus.length > 0 && !filterControlStatus.includes(c.status)) return false;
    if (filterImpl.length > 0 && !filterImpl.includes(c.implementation)) return false;
    return true;
  }), [activeControls, filterControlStatus, filterImpl]);

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
        <StatBox label="Total Controls" value={allControls.total}   color="#ffffff" />
        <StatBox label="Met"            value={allControls.met}     color="#4caf50" />
        <StatBox label="Partial"        value={allControls.partial} color="#ff9800" />
        <StatBox label="Not Met"        value={allControls.notMet}  color="#f44336" />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

        {/* Family List */}
        <Paper sx={{ width: 240, flexShrink: 0, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ padding: '10px 14px', borderBottom: '1px solid #1e3a5f' }}>
            <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Control Families</Typography>
          </Box>
          {familySummaries.map(f => {
            const isActive = selectedFamily === f.code;
            const pct = Math.round((f.met / f.controls.length) * 100);
            const color = pct >= 80 ? '#4caf50' : pct >= 60 ? '#ff9800' : '#f44336';
            return (
              <Box key={f.code} onClick={() => setSelectedFamily(f.code)} sx={{
                padding: '10px 14px',
                cursor: 'pointer',
                backgroundColor: isActive ? '#0d2137' : 'transparent',
                borderLeft: `3px solid ${isActive ? '#00bcd4' : 'transparent'}`,
                '&:hover': { backgroundColor: '#0d1f33' },
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 0.5 }}>
                  <Box>
                    <Typography component="span" sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, marginRight: 0.75 }}>{f.code}</Typography>
                    <Typography component="span" sx={{ color: isActive ? '#ffffff' : '#c0cfe0', fontSize: 12 }}>{f.name}</Typography>
                  </Box>
                  <Typography sx={{ color, fontSize: 11, fontWeight: 700 }}>{f.met}/{f.controls.length}</Typography>
                </Box>
                <Box sx={{ height: 3, borderRadius: 1, backgroundColor: '#1e3a5f' }}>
                  <Box sx={{ height: 3, borderRadius: 1, backgroundColor: color, width: `${pct}%` }} />
                </Box>
              </Box>
            );
          })}
        </Paper>

        {/* Controls Table */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 16, marginBottom: 1.5 }}>
            {activeFamily?.code} — {activeFamily?.name}
          </Typography>

          <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr 90px 140px 120px', padding: '10px 16px', borderBottom: '1px solid #1e3a5f', backgroundColor: '#0d1825' }}>
              <ColFilter label="Control ID"    options={[]}                                    value={[]}                   onChange={() => {}} />
              <ColFilter label="Control Name"  options={[]}                                    value={[]}                   onChange={() => {}} />
              <ColFilter label="Status"        options={['Met', 'Partial', 'Not Met']}         value={filterControlStatus}  onChange={setFilterControlStatus} />
              <ColFilter label="Implementation" options={['System Specific', 'Inherited', 'Hybrid']} value={filterImpl}    onChange={setFilterImpl} />
              <ColFilter label="Last Assessed" options={[]}                                    value={[]}                   onChange={() => {}} />
            </Box>
            {displayControls.map((c, i) => (
              <Box key={c.id} sx={{
                display: 'grid',
                gridTemplateColumns: '100px 1fr 90px 140px 120px',
                alignItems: 'center',
                padding: '12px 16px',
                borderBottom: i < displayControls.length - 1 ? '1px solid #0d1825' : 'none',
                '&:hover': { backgroundColor: '#0d1f33' },
              }}>
                <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>{c.id}</Typography>
                <Typography sx={{ color: '#c0cfe0', fontSize: 13 }}>{c.name}</Typography>
                <Chip label={c.status} size="small" sx={{
                  backgroundColor: `${STATUS_COLOR[c.status]}22`,
                  color: STATUS_COLOR[c.status],
                  fontWeight: 700, fontSize: 11, height: 20, width: 'fit-content',
                }} />
                <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>{c.implementation}</Typography>
                <Typography sx={{ color: '#4a5a70', fontFamily: 'monospace', fontSize: 12 }}>{c.lastAssessed}</Typography>
              </Box>
            ))}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
